import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, User, Mail, Phone, Users,
  AlertTriangle, Scale, Shield, Loader2, ChevronDown, ChevronRight
} from 'lucide-react';
import DataProtectionCheckbox from '../components/DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const SECTIONS = [
  {
    title: "Plazos y acciones desde la notificación del INSS",
    actor: "Trabajador/a",
    color: "emerald",
    items: [
      { time: "Día 0", title: "Anotar la fecha exacta de recepción de la notificación del INSS", detail: "El plazo de 10 días empieza en este momento. No desde que se lee, sino desde que se recibe. Guardar el sobre con matasellos si llega por correo postal.", ref: "Art. 49.1.e) ET" },
      { time: "≤ 10 días", title: "Comunicar por escrito a la empresa la voluntad de continuar", detail: "Plazo improrrogable. Por escrito, con acuse de recibo o correo certificado.", ref: "Ley 2/2025" },
      { time: "Fase II", title: "Acudir al reconocimiento médico citado por el SPA", detail: "El reconocimiento médico previo a la reincorporación es obligatorio. Llevar informes médicos que describan las limitaciones funcionales actuales.", ref: "Art. 22 LPRL" },
      { time: "Antes de firmar", title: "No firmar ningún documento de extinción sin revisión previa", detail: "Finiquitos, acuerdos de extinción o bajas voluntarias pueden incluir renuncias a acciones judiciales. Una firma apresurada puede eliminar derechos irrecuperables.", ref: "ET general" },
      { time: "20 días hábiles", title: "Plazo para demandar por despido si la extinción es irregular", detail: "Desde la comunicación de la extinción del contrato. Este plazo de caducidad no se puede recuperar.", ref: "Art. 59 ET" },
      { time: "Antes de volver", title: "Verificar que el puesto asignado es compatible con las limitaciones", detail: "Si el informe dice 'apto con limitaciones' y la empresa asigna las mismas tareas, documentar la contradicción por escrito.", ref: "Art. 25 LPRL" },
    ]
  },
  {
    title: "Plazos y obligaciones desde que el trabajador/a comunica",
    actor: "Empresa / RRHH",
    color: "blue",
    items: [
      { time: "Inmediato", title: "Notificar al SPA para iniciar la evaluación de aptitud", detail: "En cuanto se recibe la comunicación escrita del trabajador/a. No esperar semanas.", ref: "Art. 31 LPRL" },
      { time: "Fase I-II", title: "Suspender el contrato con reserva de puesto", detail: "Desde la comunicación hasta la resolución del protocolo. El puesto queda reservado y no puede cubrirse definitivamente.", ref: "Art. 48.2 ET" },
      { time: "Meses 1-2", title: "Identificar vacantes compatibles en el grupo profesional", detail: "Revisar qué puestos están disponibles o podrían adaptarse según limitaciones funcionales indicadas por el SPA.", ref: "Ley 2/2025" },
      { time: "Meses 1-2", title: "Consultar a Delegados de Prevención o Comité de Empresa", detail: "Requisito legal, no formalidad. Documentar la consulta y el resultado con acta. Su omisión puede invalidar la extinción posterior.", ref: "Art. 36 LPRL" },
      { time: "≤ 3 meses", title: "Resolver el protocolo y comunicar la decisión por escrito", detail: "Plazo máximo improrrogable. Si transcurre sin resolución, la extinción posterior puede ser declarada despido nulo.", ref: "Ley 2/2025" },
      { time: "Si reingreso", title: "Organizar formación habilitante antes de la incorporación", detail: "Tras ausencia de 6+ meses, la formación es obligatoria, práctica y presencial.", ref: "Art. 19 LPRL" },
      { time: "Si reingreso", title: "Permitir disfrute de vacaciones devengadas durante IT/IP", detail: "Las vacaciones devengadas durante IT o IP no se pierden aunque el año natural haya transcurrido.", ref: "Art. 38 ET" },
      { time: "Si extinción", title: "Acreditar carga excesiva (PYME <25) con cálculo documentado", detail: "Coste del ajuste supera el mayor entre: indemnización por despido improcedente o 6 mensualidades de salario bruto.", ref: "Ley 2/2025" },
    ]
  },
  {
    title: "Plazos y entregables obligatorios del Servicio de Prevención",
    actor: "SPA / SPP",
    color: "violet",
    items: [
      { time: "Fase I", title: "Programar reconocimiento médico de reincorporación", detail: "En cuanto la empresa notifica. Antes de la vuelta efectiva, nunca después.", ref: "Art. 22 LPRL" },
      { time: "Fase II", title: "Emitir informe de aptitud con limitaciones funcionales operativas", detail: "No basta con 'Apto' o 'No Apto'. Debe describir qué tareas no puede realizar, en qué condiciones y durante cuánto tiempo.", ref: "Art. 22 LPRL" },
      { time: "Fase II", title: "Proponer adaptaciones técnicas concretas del puesto", detail: "Alternativas específicas, no genéricas: ergonómicas, horarias o funcionales. Documentar las descartadas.", ref: "Art. 19 RSP" },
      { time: "Fase II", title: "Actualizar evaluación de riesgos del puesto adaptado", detail: "Si el trabajador/a regresa a un puesto modificado, la evaluación debe actualizarse antes del reingreso.", ref: "Art. 16 LPRL" },
      { time: "Si extinción", title: "Informe técnico acreditando imposibilidad del ajuste razonable", detail: "Sin este informe, la extinción es impugnable como despido nulo.", ref: "Ley 2/2025" },
      { time: "Post-reingreso", title: "Seguimiento periódico durante los 3 primeros meses", detail: "La reincorporación no termina el día del alta. El seguimiento protege al trabajador/a y al SPA.", ref: "Art. 22 LPRL" },
    ]
  },
  {
    title: "Límites legales de la baja IT y tránsito a IP",
    actor: "Incapacidad temporal",
    color: "amber",
    items: [
      { time: "365 días", title: "Duración máxima ordinaria de la IT", detail: "Puede prorrogarse hasta 180 días más si se presume que puede ser dado de alta.", ref: "Art. 169 LGSS" },
      { time: "545 días", title: "Duración máxima total IT (365 + prórroga 180)", detail: "Si no hay recuperación, el INSS inicia de oficio expediente de IP.", ref: "Art. 170 LGSS" },
      { time: "Desde 240 días", title: "El INSS puede iniciar expediente IP antes de los 365 días", detail: "El trabajador/a puede impugnar el alta en caso de disconformidad.", ref: "Art. 170 LGSS" },
      { time: "4 días", title: "Inicio del derecho a prestación IT (contingencias comunes)", detail: "Los 3 primeros días sin prestación salvo que el convenio lo mejore. En AT/EP desde el día siguiente.", ref: "Art. 176 LGSS" },
    ]
  },
  {
    title: "Sanciones y consecuencias del incumplimiento",
    actor: "Consecuencias legales",
    color: "red",
    items: [
      { time: "Despido nulo", title: "Extinción sin protocolo = despido nulo", detail: "Readmisión obligatoria + salarios de tramitación + posible indemnización por daños.", ref: "Art. 55.5 ET" },
      { time: "2.451 - 49.180€", title: "Incumplimiento vigilancia de la salud = infracción grave LISOS", detail: "Aplicable a empresa y SPA.", ref: "Art. 12.16 LISOS" },
      { time: "49.181 - 983.736€", title: "Informes falsos u omisión esencial = infracción muy grave", detail: "Aplicable al SPA que emita informes genéricos o inadecuados.", ref: "Art. 13.8 LISOS" },
      { time: "30-50%", title: "Recargo de prestaciones por falta de medidas de seguridad", detail: "No es asegurable. Responsabilidad exclusiva del empresario.", ref: "Art. 164 LGSS" },
      { time: "Penal", title: "Responsabilidad penal del técnico PRL (STS 563/2025)", detail: "Art. 316 CP alcanza al técnico que no cumplió su deber de garante. Prisión de 6 meses a 3 años.", ref: "Art. 316 CP" },
    ]
  }
];

const colorMap = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-600', text: 'text-emerald-700', time: 'bg-emerald-100 text-emerald-700' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-600', text: 'text-blue-700', time: 'bg-blue-100 text-blue-700' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-600', text: 'text-violet-700', time: 'bg-violet-100 text-violet-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-600', text: 'text-amber-700', time: 'bg-amber-100 text-amber-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-600', text: 'text-red-700', time: 'bg-red-100 text-red-700' },
};

export default function ChecklistPage() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', perfil: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/leads`, {
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono,
        cargo: form.perfil,
        empresa: '',
        mensaje: `Acceso Checklist Plazos Legales | Perfil: ${form.perfil}`,
        origen: 'checklist_plazos'
      });
      setUnlocked(true);
    } catch (err) {
      if (!err.response) {
        setError('Error de conexión. Compruebe su conexión a internet.');
      } else {
        setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Error al procesar. Inténtelo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checklist-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/#recursos" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={18} />
            <span className="text-sm">Volver</span>
          </Link>
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" alt="Logo" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600">Herramienta interactiva</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-3" data-testid="checklist-title">
          Checklist de plazos legales: reincorporación tras baja larga
        </h1>
        <p className="mt-3 text-slate-600">
          Lista completa de verificación con todos los plazos legales para la reincorporación tras baja larga o incapacidad permanente. Ley 2/2025 actualizada.
        </p>

        {/* Quick summary — always visible */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8 mb-10">
          {[
            { actor: 'Trabajador/a', num: '10', unit: 'días naturales', desc: 'Para comunicar voluntad de continuar' },
            { actor: 'Empresa', num: '3', unit: 'meses máximo', desc: 'Para evaluar y resolver' },
            { actor: 'SPA / SPP', num: 'Fase I', unit: 'inmediato', desc: 'Programar reconocimiento médico' },
            { actor: 'Impugnación', num: '20', unit: 'días hábiles', desc: 'Plazo para demandar despido' },
          ].map((s) => (
            <div key={s.actor} className="bg-white border border-slate-200 p-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase">{s.actor}</p>
              <p className="text-2xl font-black text-slate-900 mt-1">{s.num}</p>
              <p className="text-xs text-rose-600 font-bold">{s.unit}</p>
              <p className="text-[10px] text-slate-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Lead gate */}
        {!unlocked ? (
          <div className="bg-white border border-slate-200 p-8 mb-10" data-testid="checklist-gate">
            <div className="text-center mb-6">
              <Shield className="mx-auto text-rose-600 mb-3" size={32} />
              <h2 className="text-xl font-bold text-slate-900">Acceda al checklist completo</h2>
              <p className="text-sm text-slate-500 mt-1">Indique sus datos para ver todos los plazos y obligaciones detalladas</p>
            </div>

            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4" data-testid="checklist-form">
              {error && <p className="text-sm text-red-600 bg-red-50 p-3 border border-red-200">{error}</p>}

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Nombre *</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" required value={form.nombre}
                    onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="checklist-nombre" placeholder="Su nombre" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" required value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="checklist-email" placeholder="Su email" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Móvil <span className="text-slate-400 normal-case">(opcional)</span></label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="tel" value={form.telefono}
                    onChange={(e) => setForm(f => ({ ...f, telefono: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="checklist-telefono" placeholder="600 000 000" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Perfil *</label>
                <div className="relative">
                  <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <select required value={form.perfil}
                    onChange={(e) => setForm(f => ({ ...f, perfil: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm appearance-none"
                    data-testid="checklist-perfil">
                    <option value="">Seleccione su perfil</option>
                    <option value="Empresa / RRHH">Empresa / RRHH</option>
                    <option value="Servicio de Prevención (SPA/SPP)">Servicio de Prevención (SPA/SPP)</option>
                    <option value="Trabajador/a">Trabajador/a</option>
                  </select>
                </div>
              </div>

              <DataProtectionCheckbox checked={privacyAccepted} onChange={setPrivacyAccepted} id="checklist-privacy-consent" />

              <button type="submit" disabled={loading || !privacyAccepted}
                className="w-full flex items-center justify-center gap-2 py-4 bg-rose-600 text-white font-bold hover:bg-rose-700 transition disabled:opacity-50"
                data-testid="checklist-submit-btn">
                {loading ? <Loader2 className="animate-spin" size={20} /> : <>Acceder al checklist completo</>}
              </button>
            </form>
          </div>
        ) : (
          /* Full checklist content */
          <div className="space-y-8 mb-10" data-testid="checklist-content">
            {SECTIONS.map((section, sIdx) => {
              const c = colorMap[section.color];
              const isExpanded = expandedSections[sIdx] !== false;
              return (
                <div key={section.title} className={`border ${c.border}`}>
                  <button
                    onClick={() => toggleSection(sIdx)}
                    className={`w-full flex items-center justify-between p-5 ${c.bg} text-left`}
                    data-testid={`section-toggle-${sIdx}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold text-white px-2 py-1 ${c.badge}`}>{section.actor}</span>
                      <h2 className="font-bold text-slate-900 text-sm">{section.title}</h2>
                    </div>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {isExpanded && (
                    <div className="divide-y divide-slate-100">
                      {section.items.map((item, iIdx) => (
                        <div key={item.title} className="p-5 flex gap-4" data-testid={`checklist-item-${sIdx}-${iIdx}`}>
                          <span className={`text-[10px] font-bold px-2 py-1 h-fit flex-shrink-0 whitespace-nowrap ${c.time}`}>
                            {item.time}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.detail}</p>
                            <span className="text-[10px] text-slate-400 mt-2 inline-block">{item.ref}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* CTA */}
            <div className="bg-slate-900 p-8 text-white">
              <h3 className="text-xl font-bold mb-3">¿Necesita asesoramiento personalizado?</h3>
              <p className="text-slate-300 text-sm mb-6">
                Cada caso tiene particularidades. Nuestro equipo puede revisar su situación concreta.
              </p>
              <button onClick={() => navigate('/', { state: { scrollTo: 'contacto' } })} className="inline-block px-6 py-3 bg-rose-600 font-bold hover:bg-rose-700 transition text-white" data-testid="checklist-cta">
                Solicitar consulta
              </button>
            </div>

            <p className="text-[10px] text-slate-400 leading-relaxed">
              <strong>Base normativa:</strong> Ley 2/2025 · ET arts. 38, 48.2, 49.1.e), 55.5, 59 · LGSS arts. 164, 169, 170, 176 · LPRL arts. 16, 19, 22, 25 · RSP art. 19 · LISOS arts. 12.16, 13.8 · CP art. 316 · STS 563/2025.
              Este documento es material informativo. No constituye asesoramiento jurídico individualizado.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
